import { Organization, OrganizationMember, OrganizationInvite, OrganizationSubscription } from '../types/organization';
import { addAuditLog } from './auditLog';
import { encryptData, decryptData } from './encryption';

// Storage keys
const ORGANIZATIONS_KEY = 'installtrack_organizations';
const MEMBERS_KEY = 'installtrack_org_members';
const INVITES_KEY = 'installtrack_org_invites';
const SUBSCRIPTIONS_KEY = 'installtrack_org_subscriptions';
const CURRENT_ORG_KEY = 'installtrack_current_org';

/**
 * Get all organizations
 */
export const getOrganizations = (): Organization[] => {
  try {
    const encryptedData = localStorage.getItem(ORGANIZATIONS_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData, (key, value) => {
      if (key === 'createdAt' || key === 'updatedAt') {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting organizations:', error);
    return [];
  }
};

/**
 * Save organizations
 */
export const saveOrganizations = (organizations: Organization[]): void => {
  try {
    const encryptedData = encryptData(JSON.stringify(organizations));
    localStorage.setItem(ORGANIZATIONS_KEY, encryptedData);
  } catch (error) {
    console.error('Error saving organizations:', error);
    throw error;
  }
};

/**
 * Create a new organization
 */
export const createOrganization = (
  name: string,
  userId: string,
  plan: 'free' | 'starter' | 'professional' | 'enterprise' = 'free'
): Organization => {
  try {
    const organizations = getOrganizations();
    
    // Generate URL-friendly slug
    let slug = name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    // Ensure slug is unique
    let counter = 1;
    let finalSlug = slug;
    while (organizations.some(org => org.slug === finalSlug)) {
      finalSlug = `${slug}-${counter}`;
      counter++;
    }
    
    const now = new Date();
    
    // Create organization
    const organization: Organization = {
      id: crypto.randomUUID(),
      name,
      slug: finalSlug,
      settings: {
        theme: {
          primaryColor: '#2563eb', // blue-600
          secondaryColor: '#4f46e5', // indigo-600
        },
        features: {
          photos: true,
          calendar: true,
          api: plan !== 'free',
          powerBI: plan === 'enterprise',
          microsoftIntegration: plan !== 'free',
        },
        customization: {
          allowCustomStatuses: plan === 'enterprise',
          allowCustomFields: plan === 'enterprise',
          allowCustomWorkflows: plan === 'enterprise',
        },
        security: {
          requireMFA: false,
          passwordPolicy: {
            minLength: 8,
            requireNumbers: true,
            requireSpecialChars: true,
            requireUppercase: true,
            requireLowercase: true,
          },
          sessionTimeout: 60,
        },
        storage: {
          maxPhotosPerApartment: plan === 'free' ? 10 : plan === 'starter' ? 50 : 1000,
          maxPhotoSize: plan === 'free' ? 5 * 1024 * 1024 : 20 * 1024 * 1024, // 5MB or 20MB
          totalStorageLimit: plan === 'free' ? 1 : plan === 'starter' ? 10 : 1000, // in GB
        },
        notifications: {
          email: true,
          teams: plan !== 'free',
          slack: plan === 'enterprise',
        },
      },
      createdAt: now,
      updatedAt: now,
    };
    
    // Create owner membership
    const membership: OrganizationMember = {
      id: crypto.randomUUID(),
      organizationId: organization.id,
      userId,
      role: 'owner',
      permissions: {
        manageUsers: true,
        manageSettings: true,
        manageBuildings: true,
        manageTechnicians: true,
        manageKonzernIds: true,
        viewReports: true,
        exportData: true,
        useApi: true,
      },
      joinedAt: now,
    };
    
    // Create subscription
    const subscription: OrganizationSubscription = {
      id: crypto.randomUUID(),
      organizationId: organization.id,
      plan,
      status: 'active',
      startDate: now,
      trialEndsAt: plan !== 'free' ? new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000) : undefined, // 14 days trial
    };
    
    // Save everything
    organizations.push(organization);
    saveOrganizations(organizations);
    
    const members = getOrganizationMembers();
    members.push(membership);
    saveOrganizationMembers(members);
    
    const subscriptions = getOrganizationSubscriptions();
    subscriptions.push(subscription);
    saveOrganizationSubscriptions(subscriptions);
    
    // Set as current organization
    setCurrentOrganization(organization);
    
    // Log the creation
    addAuditLog('create', 'organization', `Organization created: ${name}`, organization.id);
    
    return organization;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Get organization members
 */
export const getOrganizationMembers = (): OrganizationMember[] => {
  try {
    const encryptedData = localStorage.getItem(MEMBERS_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData, (key, value) => {
      if (key === 'joinedAt') {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting organization members:', error);
    return [];
  }
};

/**
 * Save organization members
 */
export const saveOrganizationMembers = (members: OrganizationMember[]): void => {
  try {
    const encryptedData = encryptData(JSON.stringify(members));
    localStorage.setItem(MEMBERS_KEY, encryptedData);
  } catch (error) {
    console.error('Error saving organization members:', error);
    throw error;
  }
};

/**
 * Get organization invites
 */
export const getOrganizationInvites = (): OrganizationInvite[] => {
  try {
    const encryptedData = localStorage.getItem(INVITES_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData, (key, value) => {
      if (key === 'expiresAt' || key === 'createdAt') {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting organization invites:', error);
    return [];
  }
};

/**
 * Save organization invites
 */
export const saveOrganizationInvites = (invites: OrganizationInvite[]): void => {
  try {
    const encryptedData = encryptData(JSON.stringify(invites));
    localStorage.setItem(INVITES_KEY, encryptedData);
  } catch (error) {
    console.error('Error saving organization invites:', error);
    throw error;
  }
};

/**
 * Get organization subscriptions
 */
export const getOrganizationSubscriptions = (): OrganizationSubscription[] => {
  try {
    const encryptedData = localStorage.getItem(SUBSCRIPTIONS_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData, (key, value) => {
      if (['startDate', 'endDate', 'trialEndsAt', 'canceledAt'].includes(key) && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting organization subscriptions:', error);
    return [];
  }
};

/**
 * Save organization subscriptions
 */
export const saveOrganizationSubscriptions = (subscriptions: OrganizationSubscription[]): void => {
  try {
    const encryptedData = encryptData(JSON.stringify(subscriptions));
    localStorage.setItem(SUBSCRIPTIONS_KEY, encryptedData);
  } catch (error) {
    console.error('Error saving organization subscriptions:', error);
    throw error;
  }
};

/**
 * Get current organization
 */
export const getCurrentOrganization = (): Organization | null => {
  try {
    const orgId = localStorage.getItem(CURRENT_ORG_KEY);
    if (!orgId) return null;
    
    const organizations = getOrganizations();
    return organizations.find(org => org.id === orgId) || null;
  } catch (error) {
    console.error('Error getting current organization:', error);
    return null;
  }
};

/**
 * Set current organization
 */
export const setCurrentOrganization = (organization: Organization | null): void => {
  try {
    if (organization) {
      localStorage.setItem(CURRENT_ORG_KEY, organization.id);
    } else {
      localStorage.removeItem(CURRENT_ORG_KEY);
    }
  } catch (error) {
    console.error('Error setting current organization:', error);
    throw error;
  }
};

/**
 * Get organization by slug
 */
export const getOrganizationBySlug = (slug: string): Organization | null => {
  try {
    const organizations = getOrganizations();
    return organizations.find(org => org.slug === slug) || null;
  } catch (error) {
    console.error('Error getting organization by slug:', error);
    return null;
  }
};

/**
 * Update organization
 */
export const updateOrganization = (id: string, updates: Partial<Organization>): Organization => {
  try {
    const organizations = getOrganizations();
    const index = organizations.findIndex(org => org.id === id);
    
    if (index === -1) {
      throw new Error('Organization not found');
    }
    
    // Update organization
    const updatedOrganization = {
      ...organizations[index],
      ...updates,
      updatedAt: new Date(),
    };
    
    organizations[index] = updatedOrganization;
    saveOrganizations(organizations);
    
    // Log the update
    addAuditLog('update', 'organization', `Organization updated: ${updatedOrganization.name}`, id);
    
    return updatedOrganization;
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

/**
 * Delete organization
 */
export const deleteOrganization = (id: string): void => {
  try {
    const organizations = getOrganizations();
    const organizationToDelete = organizations.find(org => org.id === id);
    
    if (!organizationToDelete) {
      throw new Error('Organization not found');
    }
    
    // Remove organization and all related data
    const filteredOrganizations = organizations.filter(org => org.id !== id);
    saveOrganizations(filteredOrganizations);
    
    const members = getOrganizationMembers().filter(member => member.organizationId !== id);
    saveOrganizationMembers(members);
    
    const invites = getOrganizationInvites().filter(invite => invite.organizationId !== id);
    saveOrganizationInvites(invites);
    
    const subscriptions = getOrganizationSubscriptions().filter(sub => sub.organizationId !== id);
    saveOrganizationSubscriptions(subscriptions);
    
    // If this was the current organization, clear it
    const currentOrg = getCurrentOrganization();
    if (currentOrg?.id === id) {
      setCurrentOrganization(null);
    }
    
    // Log the deletion
    addAuditLog('delete', 'organization', `Organization deleted: ${organizationToDelete.name}`, id);
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};